import { adminRpcDataset, Dataset } from "../../model/qubes/dataset";
import { createForm, CreateFormOptions, FieldOverride } from "./dataset-form";
import { FormContext, FormItem } from "./form-builder.model";
import { ValidationIssue } from "./validation";
import { LookupCache } from "../../services/lookup-cache";
import { notify } from "../notifications/NotificationProvider";
import {
  ACCESS_MODES,
  VALIDATION_ACCESS_MODES,
  EndpointAuthDef,
  TableAccessDef,
  TableFieldsDef,
  TableJoinsDef,
  TableMetadataDef,
  TableValidationDef,
} from "./firewall-model";
import { accessModesControl } from "./widgets/access-modes-control";
import "./widgets/sql-expression-widget"; // registers the "sql-expression" form widget

// ── SQL-firewall entity edit forms ──────────────────────────────────────────
// FEATURE-20260716-sql-firewall-policy-editor
//
// One open*Form per firewall entity, all through createForm (the dataset form
// path) with the page's FormContext threaded in. Every entry point passes
// through the OWNERSHIP GUARD first: rows with an owner (external_maintainer:
// file:<path>, app:<name>, a policy set, or a legacy model file path; the one
// owner column every converged table carries, TASK-20261030-firewall-tables-carry-external-maintainer)
// are RECLAIMED by that owner's sync —
// a silent in-place edit would be reverted on its next run. The guard blocks
// with a pointer to the source; "detach" (explicit, confirmed) clears the
// ownership tag and opens the editor, accepting the row no longer converges.

const USERGROUPS = "qubes:usergroups";
const FW_TABLES = "continuum:firewall-tables";

function registerFirewallLookups() {
  LookupCache.register({
    key: USERGROUPS,
    sql: "SELECT uid, name FROM usergroup ORDER BY name",
    valueField: "uid",
    labelField: "name",
  });
  LookupCache.register({
    key: FW_TABLES,
    sql: "SELECT uid, table_name FROM table_metadata ORDER BY table_name",
    valueField: "uid",
    labelField: "table_name",
  });
}

// ── Ownership guard ─────────────────────────────────────────────────────────

type Ownership = { kind: "manual" } | { kind: "owned"; owner: string };

function ownershipOf(record: Record<string, unknown> | undefined): Ownership {
  if (record?.["external_maintainer"]) {
    return { kind: "owned", owner: String(record["external_maintainer"]) };
  }
  return { kind: "manual" };
}

/**
 * Gate an edit on the row's ownership. Manual rows pass straight through.
 * Owned rows prompt: cancel (default) or DETACH — the detach clears the
 * ownership tag as part of the same save, so the row is manual from then on
 * and the owning sync no longer converges (or deletes) it.
 *
 * Returns the extra columns to write on save (the cleared ownership tag when
 * detaching), or undefined when the edit is cancelled.
 */
function ownershipGate(
  ownership: Ownership,
  entityLabel: string,
): Record<string, unknown> | undefined {
  if (ownership.kind === "manual") return {};
  const owner = `'${ownership.owner}'`;
  // Both owner columns are cleared while model_source is still dual-maintained; the
  // model_source key goes when that column is dropped.
  const clear = { external_maintainer: null, model_source: null };
  const ok = window.confirm(
    `This ${entityLabel} is generated — it is owned and converged by ${owner}.\n\n` +
      `Editing it here will be silently REVERTED the next time that sync runs, ` +
      `unless you detach it from its source.\n\n` +
      `OK = DETACH and edit (the row becomes manual and will no longer converge)\n` +
      `Cancel = leave it alone (edit the source instead)`,
  );
  return ok ? clear : undefined;
}

// ── Shared form-item helpers ────────────────────────────────────────────────

/** access_modes as a checkbox set over a fixed mode list (custom render). */
function accessModesOverride(
  field: { name: string },
  allowed: readonly string[],
): FieldOverride {
  return {
    field: field as FieldOverride["field"],
    item: (i: FormItem): FormItem => ({
      ...i,
      type: "text", // the render below replaces the control entirely
      render: (ctx) => accessModesControl(ctx.value, ctx.onChange, allowed),
    }),
  };
}

const jsonArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string" && v.trim().startsWith("[")) {
    try {
      return (JSON.parse(v) as unknown[]).map(String);
    } catch {
      return [];
    }
  }
  return [];
};

function requireModes(v: unknown, fieldName: string, label: string): ValidationIssue[] {
  return jsonArray(v).length === 0
    ? [{ severity: "error", fields: [fieldName], message: `${label}: pick at least one mode.` }]
    : [];
}

// ── Datasets ────────────────────────────────────────────────────────────────

const metadataDataset = adminRpcDataset({
  cube: "table_metadata",
  model: TableMetadataDef,
});
const fieldsDataset = adminRpcDataset({
  cube: "table_fields",
  model: TableFieldsDef,
  jsonColumns: [TableFieldsDef.access_modes],
  // Calculated fields hide access_modes; the column default '{}' is not a list and the mesh rejects it.
  insertDefaults: { access_modes: "[]" },
});
const joinsDataset = adminRpcDataset({
  cube: "table_joins",
  model: TableJoinsDef,
});
const validationDataset = adminRpcDataset({
  cube: "table_validation",
  model: TableValidationDef,
  jsonColumns: [TableValidationDef.access_modes, TableValidationDef.trigger_columns],
});
const accessDataset = adminRpcDataset({
  cube: "table_access",
  model: TableAccessDef,
  jsonColumns: [TableAccessDef.access_modes],
});

// ── Guarded open ────────────────────────────────────────────────────────────

type OpenOpts = {
  /** row uid to edit; undefined = create */
  key?: string;
  /** the grid row (for the ownership gate without a second load) */
  row?: Record<string, unknown>;
  context?: FormContext;
  /** create-mode initial values (e.g. the lineage table's uid) */
  defaults?: Record<string, unknown>;
};

async function openGuarded(
  dataset: Dataset,
  entityLabel: string,
  opts: OpenOpts,
  form: Omit<CreateFormOptions, "key" | "context" | "defaults">,
): Promise<void> {
  registerFirewallLookups();
  if (opts.key) {
    const gate = ownershipGate(ownershipOf(opts.row), entityLabel);
    if (gate === undefined) return; // cancelled — leave the owned row alone
    if (Object.keys(gate).length > 0) {
      // DETACH FIRST, then edit: clear the ownership tag as its own write so
      // the form (which loads the record fresh) opens on a now-manual row.
      // Small and reversible — re-attaching is setting the tag back.
      const res = await dataset.save(gate, opts.key);
      if (!res.success) {
        notify({
          severity: "error",
          title: "Detach failed",
          message: res.error?.message ?? "Could not clear the ownership tag.",
        });
        return;
      }
      notify({
        severity: "info",
        title: "Detached",
        message: `${entityLabel} is now manual — its sync will no longer converge it.`,
      });
    }
  }
  createForm(dataset, {
    ...form,
    key: opts.key,
    context: opts.context,
    defaults: opts.defaults,
  });
}

// ── Per-entity forms ────────────────────────────────────────────────────────

export function openTableMetadataForm(opts: OpenOpts) {
  void openGuarded(metadataDataset, "table registration", opts, {
    title: opts.key ? "Edit Table Registration" : "Register Table",
    submitLabel: "Save",
    sections: [
      {
        title: "Identity",
        fields: [
          TableMetadataDef.table_name,
          TableMetadataDef.schema_name,
          TableMetadataDef.actual_table_name,
        ],
      },
      { title: "Docs", fields: [TableMetadataDef.description] },
    ],
    overrides: opts.key
      ? [
          // (schema, table_name) is the natural key firewallsync correlates by —
          // renaming is delete+insert territory, so lock them on edit.
          { field: TableMetadataDef.table_name, item: (i) => ({ ...i, disabled: true }) },
          { field: TableMetadataDef.schema_name, item: (i) => ({ ...i, disabled: true }) },
        ]
      : [],
  });
}

export function openTableFieldForm(opts: OpenOpts) {
  void openGuarded(fieldsDataset, "field", opts, {
    title: opts.key ? "Edit Field" : "New Field",
    submitLabel: "Save field",
    sections: [
      {
        title: "Field",
        fields: [
          TableFieldsDef.table_uid, // hidden: preset from the lineage table
          TableFieldsDef.column_name,
          TableFieldsDef.field_kind,
          TableFieldsDef.include_in_star,
        ],
      },
      {
        title: "Expression",
        fields: [TableFieldsDef.select_expression, TableFieldsDef.expression],
      },
      {
        title: "Generation",
        fields: [TableFieldsDef.generate_mode, TableFieldsDef.access_modes],
      },
    ],
    overrides: [
      { field: TableFieldsDef.table_uid, item: (i) => ({ ...i, hidden: true }) },
      {
        field: TableFieldsDef.select_expression,
        item: (i) => ({
          ...i,
          type: "widget",
          widget: "sql-expression",
          config: { mode: "select-expression" },
          helpText:
            "Read-side SQL for this column. Type a column name, or the table name / 'outer.' to list everything available.",
          showWhen: (v) => v["field_kind"] === "calculated",
        }),
      },
      {
        field: TableFieldsDef.expression,
        item: (i) => ({
          ...i,
          type: "widget",
          widget: "sql-expression",
          config: { mode: "generated-expression" },
          helpText:
            "Write-side value expression (generated fields): a SQL literal, queryvar.<name>, or gouid.<len>.",
          showWhen: (v) => v["field_kind"] === "generated",
        }),
      },
      {
        field: TableFieldsDef.generate_mode,
        item: (i) => ({ ...i, showWhen: (v) => v["field_kind"] === "generated" }),
      },
      {
        field: TableFieldsDef.access_modes,
        item: (i) => ({
          ...i,
          type: "text",
          render: (ctx) =>
            accessModesControl(ctx.value, ctx.onChange, [
              "insert-data",
              "update-data",
              "upsert-data",
            ]),
          showWhen: (v) => v["field_kind"] === "generated",
        }),
      },
    ],
    validate: (v) => {
      const issues: ValidationIssue[] = [];
      if (!String(v["column_name"] ?? "").trim()) {
        issues.push({ severity: "error", fields: ["column_name"], message: "Column name is required." });
      }
      if (v["field_kind"] === "calculated" && !String(v["select_expression"] ?? "").trim()) {
        issues.push({
          severity: "error",
          fields: ["select_expression"],
          message: "A calculated field needs a select expression.",
        });
      }
      if (v["field_kind"] === "generated" && !String(v["expression"] ?? "").trim()) {
        issues.push({
          severity: "error",
          fields: ["expression"],
          message: "A generated field needs a value expression.",
        });
      }
      return issues;
    },
  });
}

export function openTableJoinForm(opts: OpenOpts) {
  void openGuarded(joinsDataset, "auto-join", opts, {
    title: opts.key ? "Edit Auto Join" : "New Auto Join",
    submitLabel: "Save join",
    sections: [
      {
        title: "Join",
        fields: [
          TableJoinsDef.from_table_uid, // hidden: preset from the lineage table
          TableJoinsDef.join_name,
          TableJoinsDef.to_table_uid,
          TableJoinsDef.join_type,
          TableJoinsDef.join_expression,
        ],
      },
    ],
    overrides: [
      { field: TableJoinsDef.from_table_uid, item: (i) => ({ ...i, hidden: true }) },
      {
        field: TableJoinsDef.to_table_uid,
        item: (i) => ({
          ...i,
          type: "select",
          options: () => LookupCache.getOptions(FW_TABLES),
        }),
      },
      {
        field: TableJoinsDef.join_expression,
        item: (i) => ({
          ...i,
          type: "widget",
          widget: "sql-expression",
          config: { mode: "join" },
          helpText:
            "left = this table, right = the target (pick To Table above): e.g. left.worker_uid = right.uid. Type left. or right. to list each side's columns.",
        }),
      },
    ],
    validate: (v) => {
      const issues: ValidationIssue[] = [];
      const expr = String(v["join_expression"] ?? "");
      if (!String(v["join_name"] ?? "").trim()) {
        issues.push({ severity: "error", fields: ["join_name"], message: "Join name is required." });
      }
      if (expr && !/\bleft\.|\bright\./.test(expr)) {
        issues.push({
          severity: "warning",
          fields: ["join_expression"],
          message: "Join expressions normally reference left.<col> and right.<col>.",
        });
      }
      return issues;
    },
  });
}

export function openTableValidationForm(opts: OpenOpts) {
  void openGuarded(validationDataset, "validation rule", opts, {
    title: opts.key ? "Edit Validation" : "New Validation",
    submitLabel: "Save validation",
    sections: [
      {
        title: "Rule",
        fields: [
          TableValidationDef.table_uid, // hidden: preset from the lineage table
          TableValidationDef.validation_name,
          TableValidationDef.access_modes,
          TableValidationDef.priority,
        ],
      },
      {
        title: "Check",
        fields: [
          TableValidationDef.trigger_columns,
          TableValidationDef.query,
          TableValidationDef.valid_when_no_rows,
          TableValidationDef.message,
        ],
      },
    ],
    overrides: [
      { field: TableValidationDef.table_uid, item: (i) => ({ ...i, hidden: true }) },
      accessModesOverride(TableValidationDef.access_modes, VALIDATION_ACCESS_MODES),
      {
        field: TableValidationDef.query,
        item: (i) => ({
          ...i,
          type: "widget",
          widget: "sql-expression",
          config: { mode: "validation-query" },
          helpText:
            "SELECT <bool> with @column placeholders bound from the write (e.g. SELECT EXISTS(SELECT 1 FROM worker WHERE uid = @worker_uid)).",
        }),
      },
      {
        field: TableValidationDef.trigger_columns,
        item: (i) => ({
          ...i,
          type: "json",
          helpText: 'Columns whose presence in a write triggers this rule — e.g. ["worker_uid"].',
        }),
      },
      {
        field: TableValidationDef.message,
        item: (i) => ({ ...i, type: "textarea" }),
      },
    ],
    validate: (v) => [
      ...requireModes(v["access_modes"], "access_modes", "Runs On"),
      ...(!String(v["query"] ?? "").trim()
        ? [{ severity: "error" as const, fields: ["query"], message: "A validation query is required." }]
        : []),
      ...(!String(v["message"] ?? "").trim()
        ? [{ severity: "error" as const, fields: ["message"], message: "A failure message is required." }]
        : []),
    ],
  });
}

export function openTableAccessForm(opts: OpenOpts & { isGlobal?: boolean }) {
  const blastRadius = opts.isGlobal || (opts.row && opts.row["table_uid"] == null && opts.key);
  if (blastRadius && opts.key) {
    const ok = window.confirm(
      "This is a GLOBAL access rule — it applies to EVERY table in the firewall.\n\nEdit it anyway?",
    );
    if (!ok) return;
  }
  void openGuarded(accessDataset, "access rule", opts, {
    title: opts.key ? "Edit Access Rule" : "New Access Rule",
    submitLabel: "Save rule",
    sections: [
      {
        title: "Who & What",
        fields: [
          TableAccessDef.table_uid, // hidden: preset from the lineage table (null = global)
          TableAccessDef.name,
          TableAccessDef.usergroup_uid,
          TableAccessDef.access_modes,
        ],
      },
      {
        title: "Decision",
        fields: [
          TableAccessDef.granted,
          TableAccessDef.terminal,
          TableAccessDef.priority,
          TableAccessDef.row_filter,
        ],
      },
      { title: "Docs", fields: [TableAccessDef.reason] },
    ],
    overrides: [
      { field: TableAccessDef.table_uid, item: (i) => ({ ...i, hidden: true }) },
      {
        field: TableAccessDef.usergroup_uid,
        item: (i) => ({
          ...i,
          type: "select",
          blankIsNull: true, // "(all users)" saves as NULL, not ""
          options: async () => [
            { value: "", text: "(all users)" },
            ...(await LookupCache.getOptions(USERGROUPS)),
          ],
          helpText: "Empty = the rule applies to every caller.",
        }),
      },
      accessModesOverride(TableAccessDef.access_modes, ACCESS_MODES),
      {
        field: TableAccessDef.row_filter,
        item: (i) => ({
          ...i,
          type: "widget",
          widget: "sql-expression",
          config: { mode: "row-filter" },
          helpText:
            "Optional row-level filter injected into matching queries (SQL boolean over this table's columns).",
        }),
      },
      {
        field: TableAccessDef.reason,
        item: (i) => ({ ...i, type: "textarea" }),
      },
    ],
    validate: (v) => [
      ...(!String(v["name"] ?? "").trim()
        ? [{ severity: "error" as const, fields: ["name"], message: "Rule name is required." }]
        : []),
      ...requireModes(v["access_modes"], "access_modes", "Access modes"),
    ],
  });
}

// ── endpoint_auth (its own page — RPC endpoint grants) ─────────────────────

const endpointAuthDataset = adminRpcDataset({
  cube: "endpoint_auth",
  model: EndpointAuthDef,
});

export function openEndpointAuthForm(opts: OpenOpts) {
  void openGuarded(endpointAuthDataset, "endpoint grant", opts, {
    title: opts.key ? "Edit Endpoint Grant" : "New Endpoint Grant",
    submitLabel: "Save grant",
    sections: [
      {
        title: "Rule",
        fields: [
          EndpointAuthDef.endpoint_pattern,
          EndpointAuthDef.usergroup_uid,
          EndpointAuthDef.granted,
          EndpointAuthDef.priority,
        ],
      },
      { title: "Docs", fields: [EndpointAuthDef.reason, EndpointAuthDef.expires_at] },
    ],
    overrides: [
      {
        field: EndpointAuthDef.endpoint_pattern,
        item: (i) => ({
          ...i,
          helpText:
            'Exact ("auth.LoginBegin"), prefix ("scheduler.v1." or "auth.*"), or "*" catch-all. Exact beats prefix beats catch-all; priority breaks ties.',
        }),
      },
      {
        field: EndpointAuthDef.usergroup_uid,
        item: (i) => ({
          ...i,
          type: "select",
          blankIsNull: true,
          options: async () => [
            { value: "", text: "(all users)" },
            ...(await LookupCache.getOptions(USERGROUPS)),
          ],
        }),
      },
      {
        field: EndpointAuthDef.reason,
        item: (i) => ({ ...i, type: "textarea" }),
      },
      {
        field: EndpointAuthDef.expires_at,
        item: (i) => ({
          ...i,
          blankIsNull: true,
          helpText: "Optional ISO timestamp; empty = never expires.",
        }),
      },
    ],
    validate: (v) =>
      !String(v["endpoint_pattern"] ?? "").trim()
        ? [{ severity: "error", fields: ["endpoint_pattern"], message: "A pattern is required." }]
        : [],
  });
}
